import { Stack } from '@hurdl/design-system';
import { type ReactElement } from 'react';

/**
 * The allergen-booklet Bespoke_Page: the source PDF a reviewer checks the ingestion's
 * extracted allergen data against (menus-spec R13, R18).
 *
 * The ingestion extracts allergen marks from the restaurant's allergen booklet and
 * queues every new item and every significant change for human review on the
 * menu-workspace Bespoke_Page. A reviewer confirming a mark needs the booklet itself to
 * hand; this page shows it full-width, and the menu-workspace shows the same booklet in
 * an on-page Dialog beside the item being reviewed, so the source and the extraction are
 * read together without leaving the panel.
 *
 * It holds no menu data of its own and reaches nothing at runtime: it renders the
 * booklet in an `iframe` whose `src` the panel supplies as {@link bookletUrl}, a
 * same-origin path. The URL is a prop for the same reason the menu-workspace page takes
 * its operations as one — in a full build it is the Menus_Backend's served copy of the
 * current booklet; in this build it is a committed sample. The one `h1` is the page's;
 * the `main` landmark is the panel layout's.
 */
export function AllergenBookletPage({
  bookletUrl,
}: {
  readonly bookletUrl: string;
}): ReactElement {
  return (
    <Stack gap="large">
      <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
        Allergen booklet
      </h1>

      <p className="text-hurdl-muted-foreground">
        The source PDF the ingestion extracts allergen data from. Check a queued
        item&rsquo;s allergens against it before confirming a change.
      </p>

      {/*
        A native iframe: the panel owns no PDF renderer, and the browser's own is
        exactly right for a reviewer. The title names it for assistive technology; the
        src is the same-origin path the panel supplies, never a cross-origin literal.
      */}
      <iframe
        title="Allergen booklet PDF"
        src={bookletUrl}
        className="h-screen w-full rounded-hurdl-medium border border-border"
      />
    </Stack>
  );
}
